import { useEffect, useState } from "react";
import { nhost } from "@/lib/nhost";
import { MAINTENANCE_MODE, URGENT_WHATSAPP } from "@/lib/features";

// 🛠️ "We're doing a quick upgrade" screen.
// In "auto" mode it asks our backend a tiny question; if there's no answer, the notice appears.
// When the backend is back, the notice disappears by itself (checks again every minute).

async function backendIsUp(): Promise<boolean> {
  try {
    const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("timeout")), 8000));
    const res = (await Promise.race([nhost.graphql.request({ query: "query Ping { __typename }" }), timeout])) as {
      body?: { data?: { __typename?: string }; errors?: unknown[] };
    };
    return !!res?.body?.data?.__typename;
  } catch {
    return false;
  }
}

// A friendly technician avatar (drawn in code, so it loads instantly)
function TechAvatar() {
  return (
    <svg viewBox="0 0 160 160" className="w-28 h-28 sm:w-32 sm:h-32 drop-shadow-xl" aria-hidden="true">
      <defs>
        <linearGradient id="mn-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#6d28d9" />
        </linearGradient>
        <linearGradient id="mn-helmet" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fde047" />
          <stop offset="100%" stopColor="#f59e0b" />
        </linearGradient>
      </defs>
      <circle cx="80" cy="80" r="76" fill="url(#mn-bg)" />
      <circle cx="80" cy="80" r="76" fill="none" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="4" />
      {/* body */}
      <path d="M38 142c4-24 20-36 42-36s38 12 42 36" fill="#1e1b4b" />
      <path d="M68 108l12 14 12-14" fill="#ffffff" />
      {/* head */}
      <circle cx="80" cy="74" r="28" fill="#8d5524" />
      {/* helmet */}
      <path d="M50 70c0-20 13-32 30-32s30 12 30 32z" fill="url(#mn-helmet)" />
      <rect x="46" y="66" width="68" height="8" rx="4" fill="#f59e0b" />
      <rect x="76" y="40" width="8" height="18" rx="4" fill="#fbbf24" />
      {/* face */}
      <circle cx="70" cy="82" r="3.5" fill="#1f2937" />
      <circle cx="90" cy="82" r="3.5" fill="#1f2937" />
      <path d="M70 92c5 6 15 6 20 0" stroke="#1f2937" strokeWidth="3" fill="none" strokeLinecap="round" />
      {/* wrench */}
      <g transform="rotate(-35 122 112)">
        <rect x="116" y="96" width="10" height="40" rx="5" fill="#e5e7eb" />
        <circle cx="121" cy="94" r="11" fill="#e5e7eb" />
        <rect x="117" y="82" width="8" height="12" fill="url(#mn-bg)" />
      </g>
      {/* sparkles */}
      <path d="M30 40l3 7 7 3-7 3-3 7-3-7-7-3 7-3z" fill="#fde68a" />
      <path d="M132 34l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" fill="#ffffff" />
    </svg>
  );
}

export default function MaintenanceNotice() {
  const [show, setShow] = useState(MAINTENANCE_MODE === "on");

  useEffect(() => {
    if (MAINTENANCE_MODE !== "auto") return;
    let cancelled = false;
    const check = async () => {
      const up = await backendIsUp();
      if (!cancelled) setShow(!up);
    };
    check();
    const t = setInterval(check, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // Stop the page behind from scrolling while the notice is showing
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  if (!show) return null;

  const waText = encodeURIComponent(
    "Hello Man Aya DataLink 👋 I need urgent data.\nNetwork: \nBundle: \nPhone number: "
  );

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-gradient-to-br from-violet-950/95 via-purple-900/95 to-indigo-950/95 px-4 py-6 overflow-y-auto">
      <div className="relative w-full max-w-md">
        {/* soft glow */}
        <div className="absolute -inset-4 rounded-[2rem] bg-violet-500/30 blur-2xl" aria-hidden="true"></div>

        <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-violet-600 to-purple-700 pt-8 pb-14 flex justify-center">
            <TechAvatar />
          </div>

          <div className="-mt-8 mx-5 bg-white rounded-2xl shadow-lg px-5 py-4 text-center">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-violet-700 bg-violet-100 rounded-full px-3 py-1">
              <span className="w-2 h-2 rounded-full bg-violet-600 animate-pulse"></span>
              Upgrade in progress
            </span>
          </div>

          <div className="px-6 pt-5 pb-7 text-center">
            <h1 className="text-2xl sm:text-[1.7rem] leading-tight font-extrabold text-gray-900 font-sora">
              We&apos;re doing a quick upgrade — back soon.
            </h1>
            <p className="mt-2 text-base font-bold text-violet-700">Thank you for your patience! 💜</p>

            <div className="my-6 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>

            <p className="text-sm text-gray-700 font-semibold">
              If you still need <span className="text-gray-900 font-extrabold">urgent data</span>, chat with us on WhatsApp
              for <span className="text-gray-900 font-extrabold">manual delivery</span>:
            </p>

            <a
              href={`https://wa.me/${URGENT_WHATSAPP}?text=${waText}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex w-full items-center justify-center gap-2.5 rounded-2xl bg-[#25D366] hover:bg-[#1ebe5b] active:scale-[0.99] text-white font-extrabold text-base py-3.5 shadow-lg shadow-green-500/30 transition-all"
            >
              <svg viewBox="0 0 32 32" className="w-6 h-6" fill="currentColor" aria-hidden="true">
                <path d="M16.04 3C9 3 3.3 8.7 3.3 15.73c0 2.25.59 4.45 1.71 6.39L3.2 29l7.06-1.78a12.7 12.7 0 0 0 5.78 1.39h.01c7.03 0 12.74-5.7 12.74-12.73C28.79 8.7 23.08 3 16.04 3zm0 23.3h-.01a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-4.19 1.06 1.12-4.08-.25-.42a10.54 10.54 0 0 1-1.62-5.62c0-5.85 4.76-10.6 10.62-10.6 2.83 0 5.5 1.1 7.5 3.11a10.53 10.53 0 0 1 3.1 7.5c0 5.85-4.76 10.6-10.6 10.6zm5.82-7.94c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.71.16-.21.32-.82 1.04-1 1.25-.18.21-.37.24-.69.08-.32-.16-1.34-.49-2.56-1.57-.94-.84-1.58-1.88-1.77-2.2-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.71-1.71-.98-2.34-.26-.62-.52-.53-.71-.54h-.61c-.21 0-.56.08-.85.4-.29.32-1.11 1.09-1.11 2.66s1.14 3.08 1.3 3.29c.16.21 2.24 3.42 5.42 4.8.76.33 1.35.52 1.81.67.76.24 1.45.21 2 .13.61-.09 1.89-.77 2.15-1.52.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37z" />
              </svg>
              Chat on WhatsApp · 0247 204 860
            </a>

            <p className="mt-4 text-[11px] text-gray-400">Man Aya DataLink · manayadatalink.com</p>
          </div>
        </div>
      </div>
    </div>
  );
}
